import { nextTick } from 'vue'

import { logService, LOG_CATEGORIES } from '@/services/logService'
import {
  NATIVE_OPEN_TRACK_FILES_EVENT,
  type NativeOpenTrackFilesEvent,
} from '@/services/nativeOpenService'
import { requirePermission } from '@/services/permissionService'
import { useLibraryStore } from '@/stores/libraryStore'
import { useSearchWorkspaceStore } from '@/stores/searchWorkspaceStore'
import { useUiStore } from '@/stores/uiStore'

import type { Router } from 'vue-router'

const WORKSPACES_VIEW_ROUTES = new Set([
  'workspaces',
  'pinned',
  'library',
  'landing-page',
  'workspace-detail',
  'tour-detail',
  'photo-detail',
  'accept-invitation',
])

let queuedTrackFiles: File[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null
let batchResetTimer: ReturnType<typeof setTimeout> | null = null
let isImporting = false
let appendToQueuedUploadedBatch = false

type NativeTrackImportOptions = {
  appendToUploadedBatch?: boolean
}

function getTrackFiles(files: File[]) {
  return files.filter((file) => {
    const fileName = file.name.toLowerCase()
    return fileName.endsWith('.gpx') || fileName.endsWith('.fit')
  })
}

async function ensureWorkspaceViewMounted(router: Router) {
  const routeName = router.currentRoute.value.name
  if (typeof routeName === 'string' && WORKSPACES_VIEW_ROUTES.has(routeName)) return

  try {
    await router.push({ name: 'library' })
    await nextTick()
  } catch (error) {
    logService.warn(LOG_CATEGORIES.FILE, 'Failed to navigate to library for native file import', {
      error,
    })
  }
}

async function importNativeTrackFiles(
  router: Router,
  files: File[],
  options: NativeTrackImportOptions = {},
) {
  const trackFiles = getTrackFiles(files)
  if (trackFiles.length === 0) return 0

  if (!(await requirePermission('upload_gpx'))) return 0

  const uiStore = useUiStore()
  const libraryStore = useLibraryStore()
  const searchWorkspaceStore = useSearchWorkspaceStore()

  uiStore.hideLandingPage()
  uiStore.enableAutoViewportAdaptation()
  uiStore.hideTourDetailPanel()
  uiStore.showSidebar()
  uiStore.expandMobileBottomPanel()
  if (!options.appendToUploadedBatch) {
    libraryStore.clearSelectedTours()
  }

  await ensureWorkspaceViewMounted(router)
  searchWorkspaceStore.hideAllWorkspacesTemporary()
  uiStore.isAllActivitiesView = true
  window.dispatchEvent(new CustomEvent('open-all-activities'))
  await nextTick()

  const addedCount = await libraryStore.uploadTourFiles(trackFiles, {
    appendToUploadedBatch: options.appendToUploadedBatch,
  })
  if (addedCount === 0) return addedCount

  logService.info(LOG_CATEGORIES.FILE, 'Native opened track files added to library', {
    fileCount: trackFiles.length,
    addedCount,
  })
  return addedCount
}

async function flushQueuedTrackFiles(router: Router) {
  if (isImporting) return
  isImporting = true

  try {
    let appendToUploadedBatch = appendToQueuedUploadedBatch

    while (queuedTrackFiles.length > 0) {
      const files = queuedTrackFiles
      queuedTrackFiles = []
      const addedCount = await importNativeTrackFiles(router, files, { appendToUploadedBatch })
      if (addedCount > 0) {
        appendToUploadedBatch = true
        appendToQueuedUploadedBatch = true
      }
    }
  } catch (error) {
    logService.error(LOG_CATEGORIES.FILE, 'Native track file import failed', { error })
  } finally {
    isImporting = false
    if (queuedTrackFiles.length > 0) {
      void flushQueuedTrackFiles(router)
      return
    }

    batchResetTimer = setTimeout(() => {
      batchResetTimer = null
      appendToQueuedUploadedBatch = false
    }, 1000)
  }
}

function enqueueNativeTrackFiles(router: Router, files: File[]) {
  queuedTrackFiles.push(...files)

  if (batchResetTimer) {
    clearTimeout(batchResetTimer)
    batchResetTimer = null
  }

  if (flushTimer) clearTimeout(flushTimer)
  flushTimer = setTimeout(() => {
    flushTimer = null
    void flushQueuedTrackFiles(router)
  }, 100)
}

export function setupNativeTrackFileImport(router: Router) {
  window.addEventListener(NATIVE_OPEN_TRACK_FILES_EVENT, (event) => {
    const files = (event as NativeOpenTrackFilesEvent).detail
    enqueueNativeTrackFiles(router, files)
  })
}
